import {initializeApp} from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyBKfm5lhemkKeexrTBwmcTN3yadaa9t6Zw",
  authDomain: "crwn-clothing-db-3cf7f.firebaseapp.com",
  projectId: "crwn-clothing-db-3cf7f",
  storageBucket: "crwn-clothing-db-3cf7f.appspot.com",
  messagingSenderId: "1067159915096",
  appId: "1:1067159915096:web:cbdab768f198205a13d82d",
};

initializeApp(firebaseConfig);

const googleProvider = new GoogleAuthProvider();

googleProvider.setCustomParameters({prompt: "select_account"});

export const auth = getAuth();

export const signInWithGooglePopup = () =>
  signInWithPopup(auth, googleProvider);

export const signInWithGoogleRedirect = () =>
  signInWithRedirect(auth, googleProvider);

export const db = getFirestore();

export const getCollectionAndDocuments = async () => {
  const collectionRef = collection(db, "categories");
  const q = query(collectionRef);
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.reduce((accumulator, doc) => {
    const {title, items} = doc.data();
    accumulator[title.toLowerCase()] = items;
    return accumulator;
  }, {});
};

export const addCollectionAndDocuments = async (
  collectionKey,
  objectsToAdd
) => {
  const collectionRef = collection(db, collectionKey);
  const batch = writeBatch(db);

  objectsToAdd.forEach((obj) => {
    const newDocRef = doc(collectionRef, obj.title.toLowerCase());
    batch.set(newDocRef, obj);
  });

  await batch.commit();
  console.log("Collection added successfully");
};

export const createUserDocumentFromAuth = async (
  userAuth,
  additionalInformation = {}
) => {
  const userDocRef = doc(db, "users", userAuth.uid);
  const userDocSnap = await getDoc(userDocRef);

  if (!userDocSnap.exists()) {
    const {displayName, email} = userAuth;
    const createdAt = new Date();
    try {
      await setDoc(userDocRef, {
        displayName,
        email,
        createdAt,
        ...additionalInformation,
      });
    } catch (error) {
      console.log("Error creating user", error.message);
    }
  }
  return userDocRef;
};

export const createAuthUserWithEmailAndPassword = async (email, password) => {
  if (!email || !password) return;

  return createUserWithEmailAndPassword(auth, email, password);
};

export const signInAuthUserWithEmailAndPassword = async (email, password) => {
  if (!email || !password) return;

  return signInWithEmailAndPassword(auth, email, password);
};

export const signOutUser = async () => await signOut(auth);

/* ============================================================
   Blog posts (Firestore "blogs" collection)
   ============================================================ */

const blogsCollectionRef = collection(db, "blogs");

/**
 * Turns a post title into a URL-friendly slug, e.g.
 * "My First Post!" -> "my-first-post".
 */
export const slugify = (title = "") =>
  title
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Fetches all blog posts ordered by newest first.
 * @returns {Promise<Array>} list of posts with their `id`.
 */
export const getBlogPosts = async () => {
  const q = query(blogsCollectionRef, orderBy("createdAt", "desc"));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((d) => ({id: d.id, ...d.data()}));
};

/**
 * Fetches a single post by its slug, or null if none exists.
 */
export const getBlogPostBySlug = async (slug) => {
  const q = query(blogsCollectionRef, where("slug", "==", slug));
  const querySnapshot = await getDocs(q);
  if (querySnapshot.empty) return null;
  const d = querySnapshot.docs[0];
  return {id: d.id, ...d.data()};
};

/**
 * Creates a new blog post. Generates a unique slug from the title.
 * @returns {Promise<string>} the slug of the created post.
 */
export const createBlogPost = async (post, author) => {
  const baseSlug = slugify(post.title) || "post";
  let slug = baseSlug;

  // Ensure the slug is unique by appending a counter when needed.
  let attempt = 1;
  // eslint-disable-next-line no-await-in-loop
  while (await getBlogPostBySlug(slug)) {
    attempt += 1;
    slug = `${baseSlug}-${attempt}`;
  }

  await addDoc(blogsCollectionRef, {
    ...post,
    slug,
    authorId: author?.uid ?? null,
    authorName: author?.displayName || author?.email || "Anonymous",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return slug;
};

/**
 * Updates an existing post by its document id.
 */
export const updateBlogPost = async (id, updates) => {
  const postDocRef = doc(db, "blogs", id);
  await updateDoc(postDocRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Deletes a post by its document id.
 */
export const deleteBlogPost = async (id) => {
  const postDocRef = doc(db, "blogs", id);
  await deleteDoc(postDocRef);
};

/**
 * Registers an authentication state change listener.
 *
 * @param {function} callback - The callback function to be called when the authentication state changes.
 */
export const onAuthStateChangedListener = (callback) =>
  onAuthStateChanged(auth, callback);
